import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tooltip,
    Typography
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { Machine } from '../../../../ts/Connections';
import { AprilTag } from '../../../../ts/AprilTag';
import { useTranslation } from 'react-i18next';
import ForkliftTagScanner from './ForkliftTagScanner';

interface SelectForkliftModalProps {
    open: boolean;
    setOpenForkliftModal: React.Dispatch<React.SetStateAction<boolean>>;
    forkliftList?: AprilTag[];
    setForklift?: React.Dispatch<React.SetStateAction<AprilTag | null>>;
    machine: Machine
}

const SelectForkliftModal: React.FC<SelectForkliftModalProps> = ({
    open,
    setOpenForkliftModal,
    forkliftList = [],
    setForklift,
    machine
}) => {
    const [selectedForklift, setSelectedForklift] = useState<string>('');
    const [scanning, setScanning] = useState<boolean>(false);
    const { t } = useTranslation();

    React.useEffect(() => {
        if (!open) {
            setScanning(false);
        }
    }, [open]);

    const handleChange = (event: SelectChangeEvent<string>) => {
        setSelectedForklift(event.target.value);
    };

    const handleDetected = (detectedForklift: AprilTag) => {
        setSelectedForklift(detectedForklift.tagName);
        setScanning(false);
        if (setForklift) {
            setForklift(detectedForklift);
            setOpenForkliftModal(false);
        }
    };

    const handleSave = () => {
        if (setForklift) {
            if (selectedForklift === "") {
                setForklift(null);
            } else {
                const selectedForkliftObj = forkliftList.find(f => f.tagName === selectedForklift);
                if (selectedForkliftObj) {
                    setForklift(selectedForkliftObj);
                }
            }
            setOpenForkliftModal(false);
        }
    };

    const handleClose = () => {
        setScanning(false);
        setOpenForkliftModal(false);
        setSelectedForklift('');
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('control.inputs.selectForkliftModal.title')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <FormControl fullWidth sx={{ flexGrow: 1 }}>
                        <InputLabel id="forklift-select-label">{t('control.inputs.selectForkliftModal.forkLift')}</InputLabel>
                        <Select
                            labelId="forklift-select-label"
                            id="forklift-select"
                            value={selectedForklift}
                            onChange={handleChange}
                            label="Forklift"
                        >
                            <MenuItem value="">
                                {t('control.inputs.selectForkliftModal.noForklift')}
                            </MenuItem>
                            {forkliftList.length > 0 ? (
                                forkliftList.map((name, index) => (
                                    <MenuItem key={index} value={name.tagName}>
                                        {name.tagName}
                                    </MenuItem>
                                ))
                            ) : (
                                ""
                            )}
                        </Select>
                    </FormControl>
                    <Tooltip
                        title={scanning
                            ? t('control.inputs.selectForkliftModal.scanner.stop')
                            : t('control.inputs.selectForkliftModal.scanner.scan')}
                    >
                        <IconButton
                            onClick={() => setScanning(prev => !prev)}
                            color="primary"
                            aria-label={(scanning
                                ? t('control.inputs.selectForkliftModal.scanner.stop')
                                : t('control.inputs.selectForkliftModal.scanner.scan')) ?? 'Scan with camera'}
                            sx={{
                                flexShrink: 0,
                                height: 56,
                                width: 56,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: scanning ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
                            }}
                        >
                            <QrCodeScannerIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <ForkliftTagScanner
                    active={scanning}
                    forkliftList={forkliftList}
                    onDetected={handleDetected}
                />
                {
                    !machine.location ? 
                    <Box mt={2}>
                        <Typography variant="body2" color="warning.main">
                            {t('control.inputs.selectForkliftModal.warning')}
                        </Typography>
                    </Box>
                    :
                    ""
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    {t('control.inputs.selectForkliftModal.cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disabled={false}
                >
                    {t('control.inputs.selectForkliftModal.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SelectForkliftModal;